import { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { Instructor, CubeQuery } from "@models/ISchema";
import { instructorSearch } from "../graphql/instructor.query";
import { useDebounce } from "@common/hooks/useDebounce";

export interface InstructorOption extends Instructor {
  label: string;
  value: string;
  isDisabled: boolean;
}

interface UseInstructorSearchParams {
  courseId?: string;
  shouldFilter: boolean;
  enabled?: boolean;
}

const serializeOptions = (instructors: Instructor[]): InstructorOption[] => {
  if (!instructors?.length) return [];

  return instructors.map(instructor => ({
    ...instructor,
    label: `${instructor.code} - ${instructor.name}`,
    value: instructor.id,
    isDisabled: !instructor?.usable,
  }));
};

export const useInstructorSearch = ({
  courseId,
  shouldFilter,
  enabled = true,
}: UseInstructorSearchParams) => {
  const client = useApolloClient();
  const { scenario, origin }: IParams = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<InstructorOption[]>([]);

  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (!enabled) setSearchTerm("");
    if (!enabled || !searchTerm || debouncedSearch !== searchTerm) {
      setOptions([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    const fetchOptions = async () => {
      try {
        const { data } = await client.query({
          query: instructorSearch,
          variables: {
            originId: origin,
            scenarioId: scenario,
            filter: {
              fields: shouldFilter ? { courseId: { eq: courseId } } : undefined,
              pagination: {
                page: 1,
                size: 20,
                searchTerm: debouncedSearch,
              },
            },
          },
        });
        const dataQuery: CubeQuery = data?.cube;
        if (active) setOptions(serializeOptions(dataQuery?.instructorsAdvancedSearch?.items));
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    void fetchOptions();
    return () => {
      active = false;
    };
  }, [client, courseId, shouldFilter, enabled, scenario, origin, searchTerm, debouncedSearch]);

  return {
    options,
    loading,
    onSearch: setSearchTerm,
  };
};

export default useInstructorSearch;
